import { format } from "date-fns";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";
import { ValidationMessage } from "sj-ui-toolkit/src/components";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import type { TrainingPlaAssignment } from "./types";
import type {
  TrainingAssignmentSettingsValidationErrors,
  TrainingAssignmentSettingsValues,
} from "./TrainingAssignmentSettings";

const DEFAULT_NAME = "Personalized Assignment for {User Name} — {Month Year}";

const formatDueDate = (endDate: string | null) =>
  endDate ? format(new Date(endDate), "MMMM d, yyyy") : null;

interface TrainingReviewProps {
  selectedAssignments: TrainingPlaAssignment[];
  settings: TrainingAssignmentSettingsValues;
  onStepChange: (step: number) => void;
  validationErrors: {
    assignments: { hasError: boolean; message: string };
    settings: TrainingAssignmentSettingsValidationErrors;
  };
  isFormValid: boolean;
}

export default function TrainingReview({
  selectedAssignments,
  settings,
  onStepChange,
  validationErrors,
  isFormValid,
}: TrainingReviewProps) {
  const assignmentCount = selectedAssignments.length;
  const formattedStartDate = formatDueDate(settings.startDate);
  const formattedDate = formatDueDate(settings.endDate);

  const dueSummary = formattedDate
    ? `Each assignment is unique to its recipient. All assignments will be due on ${formattedDate}.`
    : "Each assignment is unique to its recipient.";

  return (
    <div>
      <Card className="mb-6">
        <p className="text-lg font-semibold">
          You&apos;re about to assign {assignmentCount} personalized assignment
          {assignmentCount !== 1 ? "s" : ""}.
        </p>
        <p className="text-lg">{dueSummary}</p>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="min-h-52">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h4 className="text-xl font-semibold">1. Available Assignments</h4>
              {validationErrors.assignments.hasError && (
                <>
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="training-assignments-validation-error"
                  />
                  <Tooltip target="#training-assignments-validation-error" position="right">
                    {validationErrors.assignments.message}
                  </Tooltip>
                </>
              )}
            </div>
            <Button label="Edit" onClick={() => onStepChange(0)} link />
          </div>
          <p>
            <span className="font-semibold">Recipients:</span>{" "}
            {assignmentCount > 0
              ? `${assignmentCount.toLocaleString()} user${assignmentCount !== 1 ? "s" : ""} assigned`
              : "No users selected"}
          </p>
        </Card>

        <Card className="min-h-52">
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h4 className="text-xl font-semibold">2. Assignment Settings</h4>
              {(validationErrors.settings.description?.hasError ||
                validationErrors.settings.startDate?.hasError ||
                validationErrors.settings.timeframe?.hasError) && (
                <>
                  <i
                    className={`pi pi-info-circle ${themeClasses.iconError}`}
                    style={{ fontSize: "1rem" }}
                    id="training-settings-validation-error"
                  />
                  <Tooltip target="#training-settings-validation-error" position="right">
                    <ul className="list-disc pl-4">
                      {validationErrors.settings.description?.hasError && (
                        <li>{validationErrors.settings.description.message}</li>
                      )}
                      {validationErrors.settings.startDate?.hasError && (
                        <li>{validationErrors.settings.startDate.message}</li>
                      )}
                      {validationErrors.settings.timeframe?.hasError && (
                        <li>{validationErrors.settings.timeframe.message}</li>
                      )}
                    </ul>
                  </Tooltip>
                </>
              )}
            </div>
            <Button label="Edit" onClick={() => onStepChange(1)} link />
          </div>

          <p className="mb-3">
            <span className="font-semibold">Assignment Name: </span>
            {settings.name || DEFAULT_NAME}
          </p>
          <p className="mb-3">
            <span className="font-semibold">Start Date: </span>
            {formattedStartDate || "Not set"}
          </p>
          <p className="mb-3">
            <span className="font-semibold">Due Date: </span>
            {formattedDate || "Not set"}
          </p>
          <p>
            <span className="font-semibold">Description: </span>
            {settings.description || "No description provided"}
          </p>
        </Card>
      </div>

      {!isFormValid && (
        <div className="mt-4">
          <ValidationMessage
            severity="warn"
            text="Please fill in all required fields before submitting"
          />
        </div>
      )}
    </div>
  );
}
